import React, { useContext, useMemo, useState } from 'react';
import DatasetDefinitionsContext from '../../contexts/DatasetDefinitionsContext';
import getDefaultBonusPeriod from '../BonusAppProvider/getDefaultBonusPeriod';
import getOverlappingBonusPeriod from './getOverlappingBonusPeriod';
import Typography from '../../kingpin/atoms/Typography';
import BonusPeriodsContext from 'contexts/BonusPeriodsContext';
import { useSearchParams } from 'react-router-dom';

const BonusProgramPeriodsProvider = ({
  programId,
  children,
}: {
  programId: string;
  children: React.ReactNode;
}) => {
  const { bonusPeriodsLookup, bonusPrograms } = useContext(
    DatasetDefinitionsContext,
  );
  const [searchParams] = useSearchParams();
  const program = useMemo(() => {
    return bonusPrograms.find((bp) => bp.id === programId);
  }, [bonusPrograms, programId]);

  const bonusPeriods = useMemo(() => {
    if (!program) {
      return undefined;
    }
    if (bonusPeriodsLookup[program.id]) {
      return bonusPeriodsLookup[program.id];
    }
    return undefined;
  }, [bonusPeriodsLookup, program]);

  const defaultPeriod = useMemo(() => {
    if (!bonusPeriods) {
      return undefined;
    }

    const searchPeriodId = searchParams.get('selectedBonusPeriodId');
    if (searchPeriodId) {
      // Exact match: the drilled-in period belongs to THIS program.
      const exact = bonusPeriods.find((bp) => bp.id === searchPeriodId);
      if (exact) {
        return exact;
      }

      // Otherwise the id belongs to a *different* program — the driver entity screen shows one tab
      // per bonus program but the URL carries a single `selectedBonusPeriodId`, so a drill-down
      // from (say) the 4-weekly Productivity app leaves the quarterly Safety/Fuel tabs with an id
      // that isn't in their period list. Stitch by date: find the source period across all
      // programs, then pick this program's period that overlaps it most (see
      // getOverlappingBonusPeriod). Falls through to the program default if there's no overlap.
      const sourcePeriod = Object.values(bonusPeriodsLookup)
        .flat()
        .find((bp) => bp?.id === searchPeriodId);
      if (sourcePeriod) {
        const stitched = getOverlappingBonusPeriod({
          target: sourcePeriod,
          candidates: bonusPeriods,
        });
        if (stitched) {
          return stitched;
        }
      }
    }

    return getDefaultBonusPeriod(bonusPeriods);
  }, [bonusPeriods, searchParams, bonusPeriodsLookup]);

  const [selectedPeriod, setSelectedPeriod] = useState<
    BonusTypes.BonusPeriod | undefined
  >(defaultPeriod);

  if (!program) {
    return (
      <Typography.Body type={'Body 13'}>
        Bonus program not found
      </Typography.Body>
    );
  }

  if (!bonusPeriods) {
    return (
      <Typography.Body type={'Body 13'}>
        Bonus periods not found
      </Typography.Body>
    );
  }

  if (!defaultPeriod) {
    return (
      <Typography.Body type={'Body 13'}>
        Default bonus period not found
      </Typography.Body>
    );
  }

  return (
    <BonusPeriodsContext.Provider
      value={{
        bonusPeriods,
        selectedBonusPeriod: selectedPeriod,
        setSelectedBonusPeriod: setSelectedPeriod,
      }}
    >
      {children}
    </BonusPeriodsContext.Provider>
  );
};

export default BonusProgramPeriodsProvider;
